<template>
  <div class="gallery-container">
    <GalleryBar :title="gallery.name"
                :hasPriceList="hasPriceList"
                @show-price-list="showPriceList"
                v-if="gallery.name"/>

    <div v-if="loading" class="text-center">
      <b-spinner variant="primary" label="Spinning"/>
    </div>

    <div v-else-if="!currentTabData || currentTabData.length === 0" class="gallery-no-media">
      No media available
    </div>

    <HorizontalScroller ref="horizontalScroller" v-else
                        :loading="loading"
                        :loading-more="loadingMore"
                        :has-pagination="hasPagination"
                        :scroll-disabled="shouldShowMoreButton"
                        @expand="expandGallery">
      <div class="col col-fixed" v-for="(media, index) in displayedItems" :key="index">
        <MediaItem :media="media"
                   :index="index"
                   :digital-item-id="digitalItemId"
                   :cart-enabled="activeTab === GALLERY_TAB_TYPE.UNPURCHASED"
                   :gallery="gallery"
                   :favorite-enabled="favoriteEnabled && showAll"
                   @toggle-favorite="toggleFavourite(media)"
                   @select="showAll ? openMedia(index): null"/>
      </div>
      <template #counter>
        <div v-if="shouldShowMoreButton" class="count-badge">{{ photosCount }} {{ $t('gallery.misc.elements') }}
          <Icon class="ml-2" name="arrowRight" width="18"/>
        </div>
      </template>
    </HorizontalScroller>

    <ModalMediaItem :medias="mediaList"
                    :total-medias="activeTabCount"
                    :page="mediaList.length"
                    :pagination-mode="paginationType"
                    :is-paid="activeTab === GALLERY_TAB_TYPE.UNPURCHASED"
                    :gallery-type="gallery.type"
                    :digital-item-id="digitalItemId"
                    :ref="modalMediasGalleryId"
                    :modal-id="modalMediasGalleryId"
                    :gallery="gallery"
                    :favorite-enabled="favoriteEnabled"
                    @add-to-favourites="handleAddToFavourites"
                    @remove-from-favourites="handleRemoveFromFavourites"
                    @load-more="loadMore"/>
  </div>
</template>

<script>
import BaseDisplayType from './BaseDisplayType.vue'
import '../styles/gallery-common.css'
import GalleryBar from "@/domain/galleryTree/components/GalleryBar.vue";
import MediaItem from "@/domain/galleryTree/components/MediaItem.vue";
import ModalMediaItem from "@/domain/galleryTree/components/ModaMediaItem.vue";
import HorizontalScroller from "@/domain/galleryTree/components/HorizontalScroller.vue";

export default {
  name: 'CompactHorizontalGallery',
  components: {HorizontalScroller, ModalMediaItem, MediaItem, GalleryBar},
  extends: BaseDisplayType,


  data() {
    return {
      showAll: false,
      initialDisplayCount: 7
    }
  },

  computed: {
    displayType() {
      return 'compact-horizontal'
    },

    paginationType() {
      return 'none' // Compact horizontal handles its own display logic
    },

    displayedItems() {
      if (!this.currentTabData) return []

      if (this.showAll) {
        return this.currentTabData
      }

      return this.currentTabData.slice(0, this.initialDisplayCount)
    },

    photosCount() {
      // Get total count from the paginated response
      return this.gallery && this.gallery.num_media
    },

    shouldShowMoreButton() {
      return !this.showAll && this.currentTabData && this.currentTabData.length > this.initialDisplayCount
    },

    canExpand() {
      return this.gallery && this.gallery.can_expand === true
    }
  },

  mounted() {
    if (!this.shouldShowMoreButton)
      this.expandGallery()
  },


  methods: {
    getPaginationSize() {
      return 9999 // Load all items for compact horizontal
    },

    async expandGallery() {
      this.showAll = true
      await this.$nextTick()
      this.$refs.horizontalScroller.init()
    },

    // Override notification_ready handler for CompactHorizontal behavior
    async handleNotificationReady(payload) {
      const {galleryId, notificationData} = payload

      // Only handle notifications for this gallery
      if (galleryId !== this.gallery.id) {
        return
      }

      console.log(`CompactHorizontal auto-refresh triggered for gallery ${galleryId}:`, notificationData)

      try {
        // Check if there are actually new media
        const checkResult = await this.checkNewMedia({
          galleryId: this.gallery.id,
          currentData: this.gallery,
          fullRefresh: notificationData.type === 'picacode_update'
        })

        if (checkResult && checkResult.hasNewMedia) {
          console.log(`Auto-refreshing CompactHorizontal for gallery ${galleryId}`)
          // Auto-refresh the media data instead of showing popup
          await this.loadMedia(1, false)
        }

      } catch (error) {
        console.error('Error in CompactHorizontal auto-refresh:', error)
      }
    },

    toggleFavourite(item) {
      if (item.favorite) {
        this.handleRemoveFromFavourites(item.id)
      } else {
        this.handleAddToFavourites(item.id)
      }
    }
  }
}
</script>